import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth } from '../firebase';
import { usePostHog } from 'posthog-js/react';

const AdminViewContext = createContext();

export const useAdminView = () => {
    const context = useContext(AdminViewContext);
    if (!context) {
        throw new Error('useAdminView must be used within AdminViewProvider');
    }
    return context;
};

export const AdminViewProvider = ({ children }) => {
    const [actualIsAdmin, setActualIsAdmin] = useState(false);
    const [isAdminViewMode, setIsAdminViewMode] = useState(true); // true = show admin UI when admin
    const [user, setUser] = useState(null);
    const posthog = usePostHog();

    // Check actual admin status
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
            setUser(currentUser);
            if (currentUser) {
                try {
                    const idTokenResult = await currentUser.getIdTokenResult();
                    const isAdmin = !!idTokenResult.claims.admin;
                    setActualIsAdmin(isAdmin);
                    // Reset to admin view mode when user changes
                    setIsAdminViewMode(true);
                } catch (error) {
                    console.error("Error getting ID token result:", error);
                    setActualIsAdmin(false);
                }
            } else {
                setActualIsAdmin(false);
                setIsAdminViewMode(true);
            }
        });

        return () => unsubscribe();
    }, []);

    // Toggle admin view mode (only works for actual admins)
    const toggleAdminView = useCallback(() => {
        if (actualIsAdmin) {
            const newMode = !isAdminViewMode;
            setIsAdminViewMode(newMode);
            
            posthog?.capture('admin_view_toggled', {
                new_mode: newMode ? 'admin' : 'user',
                user_id: user?.uid
            });

            // Show toast notification
            const message = newMode ? 'Admin view enabled' : 'User view enabled';
            showToast(message);
        }
    }, [actualIsAdmin, isAdminViewMode, user, posthog]);

    // Keyboard shortcut handler
    useEffect(() => {
        const handleKeyPress = (event) => {
            // Check for Ctrl+Shift+A (Windows/Linux) or Cmd+Shift+A (Mac)
            if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key === 'A') {
                event.preventDefault();
                toggleAdminView();
            }
        };

        if (actualIsAdmin) {
            window.addEventListener('keydown', handleKeyPress);
        }

        return () => {
            window.removeEventListener('keydown', handleKeyPress);
        };
    }, [actualIsAdmin, toggleAdminView]);

    // Show toast notification (simple implementation)
    const showToast = (message) => {
        const toast = document.createElement('div');
        toast.style.cssText = `
            position: fixed;
            bottom: 20px;
            right: 20px;
            background: #333;
            color: white;
            padding: 12px 24px;
            border-radius: 8px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
            font-size: 14px;
            z-index: 10000;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            animation: slideIn 0.3s ease-out;
        `;
        toast.textContent = message;
        
        // Add animation
        const style = document.createElement('style');
        style.textContent = `
            @keyframes slideIn {
                from { transform: translateX(100%); opacity: 0; }
                to { transform: translateX(0); opacity: 1; }
            }
        `;
        document.head.appendChild(style);
        document.body.appendChild(toast);

        // Remove after 3 seconds
        setTimeout(() => {
            toast.style.animation = 'slideOut 0.3s ease-out';
            toast.style.animationFillMode = 'forwards';
            setTimeout(() => {
                document.body.removeChild(toast);
                document.head.removeChild(style);
            }, 300);
        }, 3000);
    };

    // The effective admin status (what the UI should show)
    const isAdmin = actualIsAdmin && isAdminViewMode;

    const value = {
        actualIsAdmin, // Real admin status
        isAdmin, // Effective admin status for UI
        isAdminViewMode, // Current view mode
        toggleAdminView, // Function to toggle view
        user
    };

    return (
        <AdminViewContext.Provider value={value}>
            {children}
        </AdminViewContext.Provider>
    );
};
